// "Mor pankh": a peacock feather drifts down to a bansuri → light through two slits, every possibility at once → tap to observe: it all falls into place as the feather's eye → feather-light → the painting.
export const morPankh = {
  slug: "mor-pankh",
  duration: 32,
  music: "bansuri-folk",
  captionTail: "He is the observer, because of whom everything falls into place.",
  stages: [
    { id: "weightless", at: 0, hint: "weightless" },
    { id: "possibilities", at: 8, hint: "all possibilities" },
    { id: "observe", at: 15, hint: "tap to observe" },
    { id: "light", at: 22, hint: "feather-light" },
    { id: "settle", at: 27, hint: "" },
  ],
  hold: { at: 15.5, need: 0.05, auto: 10 },
  // soft iridescent bands, never harsh. Painted layers: a luminous haze; the feather falling with iridescent barbs; interference light flowing through space; the eye blooming open; the feather floating up
  layers: [
    { src: "/arcs/feather/haze.webp", wrap: 3.48, th: 0, h: 12, y: 2.8, r: 4.9, mist: 0.22, mistCol: "#cdbbe9", show: [-1, 0, 99, 99] },
    { src: "/arcs/feather/slits.webp", add: true, shimmer: 0.4, glow: 0.15, opacity: 0.5, th: -0.1, y: 1.9, w: 8.6, h: 5.8, r: 4.4, drift: [12, 15.6, -0.1, 0.12], show: [8, 10, 15.2, 16.8] },
    { src: "/arcs/feather/waves.webp", add: true, shimmer: 0.45, sway: 0.08, opacity: 0.38, th: 0.1, y: 1.7, w: 8, h: 5.3, r: 4.6, show: [9, 11.5, 15.2, 16.8] },
    { src: "/arcs/feather/feather.webp", black: true, shimmer: 0.6, sway: 0.12, th: 0.05, y: 4.2, w: 2.6, h: 3.9, r: 3.6, drift: [0, 15, -2.4, 0.08], show: [-1, 0.5, 21, 23] },
    { src: "/arcs/feather/eye.webp", add: true, shimmer: 1, glow: 0.6, th: 0.02, y: 1.8, w: 3.9, h: 3.9, r: 4.2, show: [15.2, 16.4, 21, 24] },
    { src: "/arcs/feather/feather.webp", black: true, shimmer: 0.6, sway: 0.15, th: 0.02, y: 1.5, w: 2.4, h: 3.6, r: 3.5, drift: [21, 28, 0.6, -0.18], show: [21, 23, 31, 33] },
  ],
  touch: { kind: "press", from: 15, until: 15.6 },
  line: { text: "The cause of it all, and yet he feels as light as a feather.", from: 22.5, to: 31.5, pos: "low" },
  cues: [[16, "pop"], [16.6, "pop"], [18.2, "pop"]],
  ambient: { scene: "feather", until: 99, then: [[22, "featherSwell"]] },
  look: { start: 0.45, lift: [0.5, 8], end: 0.14, yaw: [[0, 0], [32, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "feather", pos: "low" },
  speed: [[0, 1], [32, 1]],
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform float uT, uPhase, uAspect, uTileH, uRadius, uCy, uSurface;
varying vec3 vPos;
${"__NOISE__"}
float hash1(float n){ return fract(sin(n * 91.7) * 43758.5453); }
vec3 tap(vec2 uv){
  vec2 m = 1.0 - abs(1.0 - mod(uv, 2.0));
  return texture2D(uMap, mix(uCrop.xy, uCrop.zw, m)).rgb;
}
mat2 rot2(float a){ return mat2(cos(a), -sin(a), sin(a), cos(a)); }
// a peacock feather around its eye at the origin: open grows the eye, unfurl spreads the barbs, sparkle runs glints along them
vec4 feather(vec2 p, float open, float unfurl, float sparkle){
  float u = p.x, v = p.y;
  float top = 0.62, bot = -2.1;
  float s = clamp((v - bot) / (top - bot), 0.0, 1.0);
  float w = mix(0.3, 0.55, smoothstep(0.45, 0.8, s)) * (1.0 - smoothstep(0.92, 1.0, s)) * smoothstep(0.0, 0.08, s) * mix(0.55, 1.0, unfurl);
  float inside = (1.0 - smoothstep(w - 0.03, w, abs(u))) * step(bot, v) * step(v, top);
  float bi = (v - abs(u) * 0.8) * 26.0;
  float line = 1.0 - smoothstep(0.1, 0.32, abs(fract(bi) - 0.5));
  float lower = 1.0 - smoothstep(0.4, 0.55, s);
  float barb = inside * mix(0.55 + 0.45 * line, line * step(0.35, hash1(floor(bi))), lower);
  float along = abs(u) / max(w, 0.01);
  vec3 bc = mix(vec3(0.1, 0.5, 0.36), vec3(0.06, 0.55, 0.6), smoothstep(0.0, 0.4, abs(u)));
  bc = mix(bc, vec3(0.55, 0.45, 0.2), smoothstep(0.75, 1.0, along) * 0.6);
  vec4 o = vec4(bc * (0.75 + 0.35 * line), barb);
  // glints: short bright streaks running along a few barbs
  float gh = hash1(floor(bi) + floor(uT * 2.5) * 13.0);
  float streak = step(0.8, gh) * (1.0 - smoothstep(0.0, 0.08, abs(along - fract(gh * 7.0)))) * line * inside;
  o.rgb += vec3(0.85, 1.0, 0.9) * streak * sparkle;
  // the eye: a blue heart in teal, ringed in yellow, bronze and green
  if (open > 0.01) {
    float e = length(vec2(u / 0.27, (v - 0.05) / 0.34)) / open;
    vec3 ec = e < 0.36 ? vec3(0.04, 0.09, 0.42) : e < 0.52 ? vec3(0.02, 0.45, 0.62) : e < 0.68 ? vec3(0.96, 0.8, 0.24) : e < 0.84 ? vec3(0.5, 0.36, 0.14) : vec3(0.08, 0.5, 0.32);
    ec = mix(ec, vec3(0.12, 0.3, 0.85), 1.0 - smoothstep(0.18, 0.22, length(vec2(u / 0.27, (v + 0.02) / 0.34)) / open));
    float ea = 1.0 - smoothstep(0.92, 1.0, e);
    o = vec4(mix(o.rgb, ec, ea * 0.92), max(o.a, ea));
  }
  float shaft = (1.0 - smoothstep(0.006, 0.012, abs(u - 0.03 * sin(v * 1.3)))) * step(bot - 0.5, v) * step(v, top - 0.1);
  return mix(o, vec4(0.85, 0.78, 0.6, 1.0), shaft);
}
void main(){
  float n = snoise(vPos * 0.6) * 0.5 + 0.5;
  float n2 = snoise(vPos * 3.0 + 4.0) * 0.5 + 0.5;
  float th = atan(vPos.x, -vPos.z);
  float drift = smoothstep(0.0, 8.0, uT);
  float lift = smoothstep(22.0, 27.0, uT);
  float waves = smoothstep(8.5, 10.5, uT) * (1.0 - smoothstep(16.0, 19.0, uT));
  float collapse = smoothstep(16.0, 19.0, uT);
  float settle = smoothstep(26.5, 31.0, uT);
  vec3 col;
  vec2 local;
  if (uSurface < 0.5) {
    float X = th * uRadius;
    vec2 P = vec2(X, vPos.y);
    // the soft pale ground with hints of pink, light falling from above
    col = mix(vec3(0.98, 0.95, 0.92), vec3(0.97, 0.88, 0.88), smoothstep(0.4, 0.8, n)) * (0.95 + 0.05 * n2);
    col += vec3(1.0, 0.97, 0.9) * 0.06 * (0.5 + 0.5 * sin(X * 1.7 + snoise(vec3(X * 0.3, 0.0, uT * 0.05)) * 2.0)) * smoothstep(1.0, 6.0, P.y);
    col = mix(col, col * vec3(0.72, 0.72, 0.82), waves * 0.8);
    // two slits in a dark bar, high above the feather
    vec2 S1 = vec2(-0.35, 5.0), S2 = vec2(0.35, 5.0);
    float barS = smoothstep(7.5, 9.0, uT) * (1.0 - smoothstep(16.0, 18.0, uT));
    float slit = (1.0 - smoothstep(0.04, 0.06, abs(abs(X) - 0.35))) * (1.0 - smoothstep(0.18, 0.22, abs(P.y - 5.0)));
    float bar = (1.0 - smoothstep(0.05, 0.07, abs(P.y - 5.0))) * step(abs(X), 2.4);
    col = mix(col, vec3(0.2, 0.18, 0.28), bar * barS * (1.0 - slit));
    col = mix(col, vec3(1.0, 0.98, 0.9), slit * barS);
    // all possibilities: waves of coloured light spreading from both slits and interfering, uncertain
    float d1 = length(P - S1), d2 = length(P - S2);
    float front = (uT - 8.8) * 2.4;
    float reach = smoothstep(front, front - 1.2, min(d1, d2)) * step(P.y, 5.0);
    float jit = snoise(vec3(X * 0.6, P.y * 0.6, uPhase * 0.9));
    float inter = pow(cos((d1 - d2) * 9.0 + jit * 0.8), 2.0);
    float rip = 0.5 + 0.5 * cos((d1 + d2) * 4.0 - uPhase * 4.0);
    vec3 hue = 0.5 + 0.5 * cos(6.2832 * (vec3(0.0, 0.33, 0.67) + d1 * 0.12 + (d1 - d2) * 0.4 + uPhase * 0.05));
    col = mix(col, hue * 1.05, waves * reach * inter * (0.55 + 0.45 * rip) * (0.65 + 0.35 * jit) * 0.75);
    // the feather, drifting down, then lifting feather-light
    float sway = 1.0 - smoothstep(14.0, 16.0, uT);
    vec2 C = vec2(0.25 * sin(uT * 0.55) * sway + 0.15 * sin(uT * 0.6) * lift, mix(5.6, 2.55, 1.0 - pow(1.0 - drift, 2.0)) + 0.9 * lift + 0.08 * sin(uT * 0.9) * lift);
    float a = 0.25 * sin(uT * 0.5) * sway + 0.08 * sin(uT * 0.7) * lift;
    // the observer: every wave gathers into the eye
    float rr = length((P - C) * vec2(1.0, 0.8));
    float gather = exp(-pow(rr - (1.0 - collapse) * 7.0, 2.0) * 6.0) * collapse * (1.0 - collapse) * 4.0;
    col = mix(col, hue * 1.1, clamp(gather, 0.0, 1.0) * 0.8);
    vec2 q = rot2(a) * (P - C);
    if (waves > 0.01) {
      // ghosts of every place it could be, merging into one as it is observed
      vec2 o1 = vec2(-0.55, 0.12) * waves, o2 = vec2(0.5, -0.1) * waves;
      vec2 g1 = rot2(a + 0.2 * waves) * (P - C - o1);
      vec2 g2 = rot2(a - 0.2 * waves) * (P - C - o2);
      if (abs(g1.x) < 0.8 && g1.y > -2.8 && g1.y < 0.9) { vec4 G = feather(g1, 0.0, 0.55, 0.0); col = mix(col, G.rgb * hue.brg * 1.2, G.a * 0.35 * waves); }
      if (abs(g2.x) < 0.8 && g2.y > -2.8 && g2.y < 0.9) { vec4 G = feather(g2, 0.0, 0.55, 0.0); col = mix(col, G.rgb * hue.gbr * 1.2, G.a * 0.35 * waves); }
    }
    if (abs(q.x) < 0.8 && q.y > -2.8 && q.y < 0.9) {
      vec4 F = feather(q, smoothstep(16.0, 18.2, uT), smoothstep(17.0, 20.0, uT), smoothstep(18.0, 19.5, uT));
      col = mix(col, F.rgb, F.a * (1.0 - 0.45 * waves));
    }
    local = vec2(X, P.y - uCy);
  } else if (uSurface < 1.5) {
    col = mix(vec3(0.95, 0.91, 0.9), vec3(0.92, 0.86, 0.88), n);
    float fr = length(vPos.xz);
    vec3 fh = 0.5 + 0.5 * cos(6.2832 * (vec3(0.0, 0.33, 0.67) + fr * 0.2 + uPhase * 0.05));
    col = mix(col, fh, waves * 0.35 * (0.5 + 0.5 * cos(fr * 5.0 - uPhase * 3.0)));
    local = vPos.xz;
  } else {
    col = vec3(1.0, 0.98, 0.95);
    local = vPos.xz;
  }
  vec2 tile = vec2(uTileH * uAspect, uTileH);
  vec3 paint = tap(local / tile + 0.5);
  float drip = uSurface < 0.5 ? (10.0 - vPos.y) / 10.0 : 0.5 + length(vPos.xz) / (uRadius * 3.0);
  col = mix(col, paint, smoothstep(0.0, 0.25, settle * 1.3 - drip * 0.6 - n * 0.25));
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
